import React from 'react';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesPageHeader,
  AgnesTooltip,
} from '@agnes/ui';
import { Download, HelpCircle, SlidersHorizontal } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { SearchInput } from '@/components/SearchInput';
import {
  type LinhaIncidenciaNatureza,
  useIncidenciaNaturezaStore,
} from '@/stores/useIncidenciaNaturezaStore';
import IncidenciaNaturezaFilter from './IncidenciaNaturezaFilter';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const PISPASEP_AJUDA =
  'Código relacionado ao recolhimento de encargos destinados ao Programa de Integração Social (PIS) ' +
  'ou ao Programa de Formação do Patrimônio do Servidor Público (PASEP).';

// códigos de incidência: texto puro separado por vírgula (ex.: "53, 54, 55"), vazio vira "-".
// nowrap p/ não quebrar em várias linhas na célula estreita (a tabela já rola no eixo x).
const IncCell = ({ v }: { v: string }) => <span className="whitespace-nowrap">{v ? v : '-'}</span>;

export default function PageIncidenciaNatureza() {
  const lista = useIncidenciaNaturezaStore((s) => s.lista);
  const total = useIncidenciaNaturezaStore((s) => s.total);
  const page = useIncidenciaNaturezaStore((s) => s.page);
  const size = useIncidenciaNaturezaStore((s) => s.size);
  const carregando = useIncidenciaNaturezaStore((s) => s.carregando);
  const exportando = useIncidenciaNaturezaStore((s) => s.exportando);
  const busca = useIncidenciaNaturezaStore((s) => s.busca);
  const sortBy = useIncidenciaNaturezaStore((s) => s.sortBy);
  const sortOrder = useIncidenciaNaturezaStore((s) => s.sortOrder);
  const filtros = useIncidenciaNaturezaStore((s) => s.filtros);
  const opcoes = useIncidenciaNaturezaStore((s) => s.opcoes);

  React.useEffect(() => {
    useIncidenciaNaturezaStore.getState().carregarOpcoes();
    useIncidenciaNaturezaStore.getState().buscar();
  }, []);

  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  const columns: AgnesDataTableColumn<LinhaIncidenciaNatureza>[] = [
    { key: 'codigo', header: 'Código', width: 110, sortable: true, render: (r) => r.codigo },
    { key: 'nome', header: 'Nome', width: 280, sortable: true, render: (r) => r.nome },
    { key: 'situacao', header: 'Situação', width: 120, sortable: true, render: (r) => r.situacao },
    {
      key: 'tipo',
      header: 'Tipo',
      width: 90,
      sortable: true,
      render: (r) => <IncCell v={r.tipo} />,
    },
    { key: 'cp', header: 'CP', width: 110, sortable: true, render: (r) => <IncCell v={r.cp} /> },
    {
      key: 'irrf',
      header: 'IRRF',
      width: 110,
      sortable: true,
      render: (r) => <IncCell v={r.irrf} />,
    },
    {
      key: 'fgts',
      header: 'FGTS',
      width: 110,
      sortable: true,
      render: (r) => <IncCell v={r.fgts} />,
    },
    {
      key: 'cprp',
      header: 'CPRP',
      width: 110,
      sortable: true,
      render: (r) => <IncCell v={r.cprp} />,
    },
    {
      key: 'pisPasep',
      header: 'PIS/PASEP',
      width: 130,
      sortable: true,
      headerRender: () => (
        <span className="inline-flex items-center gap-1">
          PIS/PASEP
          <AgnesTooltip content={PISPASEP_AJUDA}>
            <HelpCircle
              className="h-3.5 w-3.5 text-agnes-text-muted"
              aria-label="Ajuda PIS/PASEP"
            />
          </AgnesTooltip>
        </span>
      ),
      render: (r) => <IncCell v={r.pisPasep} />,
    },
    {
      key: 'teto',
      header: 'Teto remuneratório',
      width: 160,
      sortable: true,
      render: (r) => r.teto,
    },
    {
      key: 'descricao',
      header: 'Descrição',
      width: 320,
      sortable: true,
      render: (r) => (
        <span className="block max-w-[300px] truncate" title={r.descricao}>
          {r.descricao || '-'}
        </span>
      ),
    },
    { key: 'inicio', header: 'Início', width: 120, sortable: true, render: (r) => r.inicio || '-' },
    {
      key: 'termino',
      header: 'Término',
      width: 120,
      sortable: true,
      render: (r) => r.termino || '-',
    },
    {
      key: 'alteradoPor',
      header: 'Alterado por',
      width: 160,
      sortable: true,
      render: (r) => r.alteradoPor || '-',
    },
    {
      key: 'atualizacao',
      header: 'Atualização',
      width: 170,
      sortable: true,
      render: (r) => r.atualizacao || '-',
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<SlidersHorizontal />}
        title="Incidência por Natureza"
        subtitle="Gerenciamento de padrões adotados pela equipe técnica"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Folha de pagamento' },
          { label: 'Rubricas', href: '/esocial/rubricas' },
          { label: 'Configurações' },
          { label: 'Incidências por Natureza' },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="mb-3 flex items-center gap-2">
            <SearchInput
              value={busca}
              onValueChange={(v) => useIncidenciaNaturezaStore.getState().setBusca(v)}
              onSearch={() => useIncidenciaNaturezaStore.getState().buscar()}
              placeholder="Pesquisar por código nome ou descrição"
            />
            <div className="ml-auto flex gap-2">
              <IncidenciaNaturezaFilter
                opcoes={opcoes}
                count={filtrosAtivos}
                onApply={(f) => useIncidenciaNaturezaStore.getState().aplicarFiltros(f)}
              />
              <ExportButton
                variant="outlined"
                startIcon={<Download className="h-4 w-4" />}
                disabled={exportando}
                onClick={() => useIncidenciaNaturezaStore.getState().exportar()}
              >
                Exportar
              </ExportButton>
            </div>
          </div>

          <TableWrapper>
            <AgnesDataTable<LinhaIncidenciaNatureza>
              data-qa="incidencia-natureza-tabela"
              columns={columns}
              data={lista}
              rowKey={(r) => r.codigo}
              loading={carregando}
              sort={{
                sortBy,
                sortOrder,
                onSortChange: (by, order) =>
                  useIncidenciaNaturezaStore.getState().setSort(by, order),
              }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: (p: number) => useIncidenciaNaturezaStore.getState().setPage(p),
                onPageSizeChange: (s: number) => useIncidenciaNaturezaStore.getState().setSize(s),
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
